import React, { useState, useEffect } from "react";
import {
  GoogleMap,
  Marker,
  Polygon,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";
import IconButton from "@mui/material/IconButton";
import { PLACES_LOADER_OPTIONS } from "../../utils/googleMaps";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { EmptyState, LoadingState } from "../../components/UI/States";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point, polygon } from "@turf/helpers";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const center = { lat: 41.7, lng: -86.5 };

export default function App() {
  const { isLoaded } = useJsApiLoader(PLACES_LOADER_OPTIONS);

  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [address, setAddress] = useState("");
  const [savedLocations, setSavedLocations] = useState([]);
  const [autocomplete, setAutocomplete] = useState(null);
  const [geojson, setGeojson] = useState(null);
  const [loads, setLoads] = useState({ irc: null, ibc: null });
  const [showPolygons, setShowPolygons] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("savedLocations");
    if (stored) setSavedLocations(JSON.parse(stored));
  }, []);

  useEffect(() => {
    fetch("/SnowLoad.json")
      .then((res) => res.json())
      .then((data) => setGeojson(data));
  }, []);

  const getLoadsForPoint = (lat, lng) => {
    if (!geojson) return { irc: null, ibc: null };

    const pt = point([lng, lat]);
    for (const feature of geojson.features) {
      const poly = polygon(feature.geometry.coordinates);
      if (booleanPointInPolygon(pt, poly)) {
        return {
          irc: feature.properties.SnowLoadIRC || null,
          ibc: feature.properties.SnowLoadIBC || null,
        };
      }
    }
    return { irc: null, ibc: null };
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const foundLoads = getLoadsForPoint(lat, lng);

    setMarker({ lat, lng });
    setLoads(foundLoads);
    getAddress(lat, lng);
  };

  const getAddress = (lat, lng) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setAddress(results[0].formatted_address);
      } else {
        setAddress("Unknown Address");
      }
    });
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const foundLoads = getLoadsForPoint(lat, lng);

        setMarker({ lat, lng });
        setLoads(foundLoads);
        setAddress(place.formatted_address || "Unknown Address");
        map.panTo({ lat, lng });
      }
    }
  };

  const clearMarker = () => {
    setMarker(null);
    setAddress("");
    setLoads({ irc: null, ibc: null });
  };

  const saveLocation = () => {
    if (!marker || !address) return;
    const newLocation = {
      id: Date.now(),
      lat: marker.lat,
      lng: marker.lng,
      address,
      snowLoadIRC: loads.irc ? `${loads.irc} psf` : "N/A",
      snowLoadIBC: loads.ibc ? `${loads.ibc} psf` : "N/A",
    };
    const updated = [...savedLocations, newLocation];
    setSavedLocations(updated);
    localStorage.setItem("savedLocations", JSON.stringify(updated));
  };

  const removeLocation = (id) => {
    const updated = savedLocations.filter((loc) => loc.id !== id);
    setSavedLocations(updated);
    localStorage.setItem("savedLocations", JSON.stringify(updated));
  };

  if (!isLoaded)
    return (
      <div className="app-card rounded-2xl border">
        <LoadingState label="Loading..." />
      </div>
    );

  return (
    <div className="app-card flex flex-col overflow-hidden rounded-2xl border lg:h-[80vh] lg:flex-row">
      {/* Sidebar */}
      <div className="app-divider shrink-0 space-y-5 overflow-y-auto border-b p-4 lg:w-80 lg:border-b-0 lg:border-r">
        {/* Search */}
        <div>
          <Autocomplete
            onLoad={(ac) => setAutocomplete(ac)}
            onPlaceChanged={onPlaceChanged}
          >
            <div className="relative">
              <SearchIcon
                fontSize="small"
                className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <input
                type="text"
                placeholder="Search address"
                aria-label="Search address"
                className="app-input w-full rounded-xl border py-2.5 pl-9 pr-3 text-sm"
              />
            </div>
          </Autocomplete>
          <p className="app-muted mt-2 text-xs">
            Search for an address or click anywhere on the map to look up its
            snow load.
          </p>
        </div>

        {/* Map actions */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={saveLocation}
            className="app-btn app-btn-primary app-btn-sm"
          >
            Save
          </button>
          <button
            type="button"
            onClick={clearMarker}
            className="app-btn app-btn-secondary app-btn-sm"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setShowPolygons((prev) => !prev)}
            className="app-btn app-btn-ghost app-btn-sm"
          >
            {showPolygons ? "Hide Overlay" : "Show Overlay"}
          </button>
        </div>

        {/* Active Marker Info */}
        {marker ? (
          <div className="app-surface rounded-xl border p-3">
            <h2 className="app-eyebrow mb-1.5">Active Marker</h2>
            <p className="text-sm font-medium">{address}</p>
            <p className="app-muted text-xs tabular-nums">
              Lat: {marker.lat.toFixed(4)}, Lng: {marker.lng.toFixed(4)}
            </p>
            <p className="mt-2 text-sm font-semibold tabular-nums">
              IRC: {loads.irc ? `${loads.irc} psf` : "N/A"} | IBC:{" "}
              {loads.ibc ? `${loads.ibc} psf` : "N/A"}
            </p>
          </div>
        ) : (
          <div className="app-surface rounded-xl border">
            <EmptyState
              compact
              icon={PlaceOutlinedIcon}
              title="No marker selected."
            />
          </div>
        )}

        {/* Saved Locations */}
        <div>
          <h2 className="mb-2 font-semibold">Saved Locations</h2>
          {savedLocations.length === 0 ? (
            <p className="app-muted flex items-center gap-1.5 text-sm">
              <BookmarkBorderIcon sx={{ fontSize: 16 }} />
              No saved locations.
            </p>
          ) : (
            <ul className="space-y-2">
              {savedLocations.map((loc) => (
                <li
                  key={loc.id}
                  className="app-surface flex items-center justify-between gap-2 rounded-xl border p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{loc.address}</p>
                    <p className="app-muted text-xs tabular-nums">
                      Lat: {loc.lat.toFixed(4)}, Lng: {loc.lng.toFixed(4)}
                    </p>
                    <p className="text-sm tabular-nums">
                      IRC: {loc.snowLoadIRC} | IBC: {loc.snowLoadIBC}
                    </p>
                  </div>
                  <IconButton
                    onClick={() => removeLocation(loc.id)}
                    color="error"
                    size="small"
                    aria-label={`Remove ${loc.address}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="relative h-[60vh] lg:h-auto lg:flex-1">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={8}
          onLoad={(map) => setMap(map)}
          onClick={handleMapClick}
        >
          {/* Restore polygon overlays */}
          {showPolygons &&
            geojson &&
            geojson.features.map((feature, idx) => (
              <Polygon
                key={idx}
                paths={feature.geometry.coordinates[0].map(([lng, lat]) => ({
                  lat,
                  lng,
                }))}
                options={{
                  strokeColor: feature.properties.stroke || "#555",
                  strokeOpacity: feature.properties["stroke-opacity"] || 1,
                  strokeWeight: feature.properties["stroke-width"] || 2,
                  fillColor: feature.properties.fill || "#000",
                  fillOpacity: feature.properties["fill-opacity"] || 0.4,
                  clickable: false,
                }}
              />
            ))}

          {marker && (
            <Marker
              position={marker}
              animation={window.google.maps.Animation.DROP}
            />
          )}
        </GoogleMap>
      </div>
    </div>
  );
}
